
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { CommunicationTemplate } from '@/services/communication/templates';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Mail, Phone, Clock, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TemplateCardProps {
  template: CommunicationTemplate;
  onClick?: () => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onClick }) => {
  const getStatusBorderColor = (status?: string) => {
    switch (status) {
      case 'new': return 'border-l-blue-500';
      case 'contacted': return 'border-l-purple-500';
      case 'qualified': return 'border-l-green-500';
      case 'proposal': return 'border-l-yellow-500';
      case 'negotiation': return 'border-l-orange-500';
      case 'closed_won': return 'border-l-emerald-500';
      case 'closed_lost': return 'border-l-red-500';
      default: return 'border-l-muted';
    }
  };

  const truncateContent = (content: string, maxLength = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString: string | Date) => {
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <Card 
      className={cn(
        "transition-all duration-200 h-full flex flex-col group relative border-l-4",
        "hover:shadow-lg",
        template.isDefault && "bg-gradient-to-br from-primary/5 to-accent/5",
        getStatusBorderColor(template.leadStatus)
      )}
    >
      {/* Icon indicator - top right */}
      <div className="absolute top-3 right-3 text-muted-foreground/50 group-hover:text-primary transition-colors">
        {template.type === 'email' ? (
          <Mail className="h-4 w-4" />
        ) : (
          <Phone className="h-4 w-4" />
        )}
      </div>

      <CardHeader className="pb-3">
        <div className="flex items-start gap-2">
          {template.isDefault && (
            <Badge variant="secondary" className="text-xs flex-shrink-0">
              Default
            </Badge>
          )}
        </div>
        <CardTitle className="text-base font-semibold pr-8 mt-1">{template.name}</CardTitle>
        {template.category && (
          <Badge variant="outline" className="text-xs w-fit mt-2">
            {template.category}
          </Badge>
        )}
      </CardHeader>
      
      <CardContent className="flex-grow relative pb-4">
        <div className="text-sm text-muted-foreground line-clamp-3 relative">
          {truncateContent(template.content, 120)}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent pointer-events-none" />
      </CardContent>
      
      <CardFooter className="pt-2 text-xs text-muted-foreground flex justify-between items-center border-t">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatDate(template.updatedAt)}
        </span>
        {template.usageCount !== undefined && template.usageCount > 0 && (
          <span className="flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            {template.usageCount}
          </span>
        )}
      </CardFooter>
    </Card>
  );
};

export default TemplateCard;
